import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAvailableModules } from "shared/api/hooks/useAvailableModules";
import { ROUTES } from "shared/config";
import Icons from "shared/icons";
import { NavbarItem } from "../NavbarItem/NavbarItem";
import { useTranslation } from "react-i18next";

const linkData = {
  labelTranslationKey: "card_expenses",
  icon: <FontAwesomeIcon icon={Icons.creditCard} />,
  links: {
    amexBoa: {
      labelTranslationKey: "amex_boa",
      link: ROUTES.cards.card_requests_queue_user,
    },
    corporate: {
      labelTranslationKey: "corporate_cards",
      link: ROUTES.cards.corporate_cards_requests_queue_user,
    },
  },
};

const CardExpensesLink = ({ displayOn = "navbar" }) => {
  const { data: modules } = useAvailableModules();
  const { t } = useTranslation("Shared");
  const { pathname } = useLocation();

  const [anchorEl, setAnchorEl] = useState(null);

  const amexBoaCardsAvailable = modules?.user_modules?.CreditCards?.available;
  const corporateCardsAvailable =
    modules?.user_modules?.CorporateCards?.available;

  const availableModules =
    amexBoaCardsAvailable && corporateCardsAvailable
      ? "both"
      : amexBoaCardsAvailable
      ? "amexBoa"
      : corporateCardsAvailable
      ? "corporate"
      : null;

  const isNavbarLinkActive = [
    linkData.links.amexBoa.link,
    linkData.links.corporate.link,
  ].includes(pathname);

  const handleNavbarLinkClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNavbarMenuClose = () => {
    setAnchorEl(null);
  };

  const renderNavbarMenuItems = () =>
    Object.values(linkData.links).map(({ labelTranslationKey, link }) => (
      <MenuItem
        sx={{ "&:hover": { color: "black" } }}
        component={Link}
        to={link}
        key={link}
        onClick={handleNavbarMenuClose}
      >
        <Typography>{t(labelTranslationKey)}</Typography>
      </MenuItem>
    ));

  const renderDrawerMenuItems = () =>
    Object.values(linkData.links).map(({ labelTranslationKey, link }) => (
      <ListItemButton component={Link} to={link} key={link} sx={{ pl: 8 }}>
        <ListItemText primary={t(labelTranslationKey)} />
      </ListItemButton>
    ));

  if (displayOn === "drawer" && availableModules === "both") {
    return (
      <>
        <ListItem>
          {linkData.icon}
          <ListItemText
            primary={t(linkData.labelTranslationKey)}
            className="ms-3"
          />
        </ListItem>
        <List component="div" disablePadding>
          {renderDrawerMenuItems()}
        </List>
      </>
    );
  }

  if (
    displayOn === "drawer" &&
    ["amexBoa", "corporate"].includes(availableModules)
  ) {
    return (
      <ListItem id="card-expenses-link" disablePadding>
        <ListItemButton
          component={Link}
          to={linkData.links[availableModules].link}
        >
          {linkData.icon}
          <ListItemText
            primary={t(linkData.labelTranslationKey)}
            className="ms-3"
          />
        </ListItemButton>
      </ListItem>
    );
  }

  if (displayOn === "navbar" && availableModules === "both") {
    return (
      <>
        <div
          role="button"
          onClick={handleNavbarLinkClick}
          className={isNavbarLinkActive ? "text-light nav-item" : "nav-item"}
        >
          {linkData.icon}
          {t(linkData.labelTranslationKey)}
        </div>
        <Menu
          sx={{ mt: "45px" }}
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          keepMounted
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          disableScrollLock
          open={Boolean(anchorEl)}
          onClose={handleNavbarMenuClose}
        >
          {renderNavbarMenuItems()}
        </Menu>
      </>
    );
  }

  if (
    displayOn === "navbar" &&
    ["amexBoa", "corporate"].includes(availableModules)
  ) {
    return (
      <NavbarItem
        link={linkData.links[availableModules].link}
        label={t(linkData.labelTranslationKey)}
        icon={linkData.icon}
      />
    );
  }

  return null;
};

export default CardExpensesLink;
